import { useMemo, useState } from "react";
import { CALCULATOR } from "@/data/content";
import { SectionHeading, CTAButton } from "./ui";
import { Reveal } from "./Reveal";

function Field({ label, value, onChange, suffix, min, max, step = 1, testid }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="text-sm font-medium text-brand-graphite/80">{label}</label>
        <span className="font-mono text-sm text-brand-bordeaux">
          {value}
          {suffix ? ` ${suffix}` : ""}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="mt-3 w-full accent-brand-bordeaux cursor-pointer"
      />
    </div>
  );
}

const euro = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const num = (n) => new Intl.NumberFormat("it-IT").format(Math.round(n));

export default function ROICalculator() {
  const [people, setPeople] = useState(3);
  const [hours, setHours] = useState(24);
  const [rate, setRate] = useState(28);
  const [leads, setLeads] = useState(40);
  const [noFollow, setNoFollow] = useState(45);
  const [clientValue, setClientValue] = useState(600);

  const r = useMemo(() => {
    const annualHours = people * hours * 12;
    const annualCost = annualHours * rate;
    const lostLeads = Math.round(leads * (noFollow / 100) * 12);
    const potential = lostLeads * clientValue;
    return { annualHours, annualCost, lostLeads, potential };
  }, [people, hours, rate, leads, noFollow, clientValue]);

  return (
    <section id="roi" className="relative bg-brand-cream py-20 lg:py-32 grain-overlay">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <SectionHeading eyebrow={CALCULATOR.eyebrow} title={CALCULATOR.title} />

        <div className="mt-14 grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Inputs */}
          <Reveal>
            <div className="rounded-sm border border-brand-bordeaux/12 bg-brand-ivory p-8 lg:p-10 space-y-7" data-testid="roi-inputs">
              <Field label="Persone coinvolte" value={people} onChange={setPeople} min={1} max={20} suffix="pers." testid="roi-people" />
              <Field label="Ore mensili su attività ripetitive" value={hours} onChange={setHours} min={2} max={160} suffix="h" testid="roi-hours" />
              <Field label="Costo medio orario" value={rate} onChange={setRate} min={10} max={80} suffix="€/h" testid="roi-rate" />
              <Field label="Lead mensili" value={leads} onChange={setLeads} min={5} max={500} step={5} testid="roi-leads" />
              <Field label="Lead senza follow-up" value={noFollow} onChange={setNoFollow} min={0} max={100} suffix="%" testid="roi-nofollow" />
              <Field label="Valore medio cliente (opzionale)" value={clientValue} onChange={setClientValue} min={50} max={5000} step={50} suffix="€" testid="roi-clientvalue" />
            </div>
          </Reveal>

          {/* Results */}
          <Reveal delay={1}>
            <div className="rounded-sm bg-brand-graphite p-8 lg:p-10 grain-overlay relative" data-testid="roi-results">
              <span className="text-[10px] uppercase tracking-editorial font-semibold text-brand-gold">
                Simulazione
              </span>
              <div className="mt-6 grid grid-cols-2 gap-6">
                <div>
                  <p className="font-serif text-4xl lg:text-5xl text-brand-ivory" data-testid="roi-annual-hours">{num(r.annualHours)}</p>
                  <p className="mt-1 text-xs text-brand-ivory/60">Ore annuali assorbite</p>
                </div>
                <div>
                  <p className="font-serif text-4xl lg:text-5xl text-brand-gold" data-testid="roi-annual-cost">{euro(r.annualCost)}</p>
                  <p className="mt-1 text-xs text-brand-ivory/60">Costo operativo stimato</p>
                </div>
                <div>
                  <p className="font-serif text-4xl lg:text-5xl text-brand-ivory" data-testid="roi-lost-leads">{num(r.lostLeads)}</p>
                  <p className="mt-1 text-xs text-brand-ivory/60">Lead non seguiti / anno</p>
                </div>
                <div>
                  <p className="font-serif text-4xl lg:text-5xl text-[#7fd6a0]" data-testid="roi-potential">{euro(r.potential)}</p>
                  <p className="mt-1 text-xs text-brand-ivory/60">Valore potenziale del processo</p>
                </div>
              </div>

              <p className="mt-8 text-[11px] leading-relaxed text-brand-ivory/50 border-t border-brand-ivory/10 pt-5">
                {CALCULATOR.disclaimer}
              </p>

              <div className="mt-6">
                <CTAButton href="#lead" variant="gold" className="w-full" data-testid="roi-cta">
                  {CALCULATOR.cta}
                </CTAButton>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
